// src/pages/IndustryPage.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, CardActionArea,
  Tabs, Tab, Divider, Container, Breadcrumbs, Link, Paper,
  Avatar, Chip, Button, useTheme, alpha,
  List, ListItem, ListItemIcon, ListItemText, CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  NavigateNext as NavigateNextIcon,
  Dashboard as DashboardIcon,
  Gavel as GavelIcon,
  Lightbulb as LightbulbIcon,
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
  ArrowForward as ArrowForwardIcon,
  People as PeopleIcon,
  MonetizationOn as MonetizationOnIcon,
  Science as ScienceIcon,
  Article as ArticleIcon,
  Check as CheckIcon,
  ShowChart as ShowChartIcon,
  Stars as StarsIcon,
  Assessment as AssessmentIcon,
  Insights as InsightsIcon
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';

// Import API service
import * as industryApi from '../services/industry';

import ValueChainTable from '../components/valueChain/ValueChainTable';

const MotionCard = motion(Card);
const MotionPaper = motion(Paper);
const MotionBox = motion(Box);

const IndustryPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [industry, setIndustry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);
  const [ecosystemData, setEcosystemData] = useState([]);
  const theme = useTheme();

  useEffect(() => {
    const fetchIndustry = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await industryApi.getIndustryById(id, {
          includeSubIndustries: true,
          includeValueChain: true,
          includeSupportEcosystem: true
        });

        if (data) {
          setIndustry(data);

          // Fetch support ecosystem data
          try {
            const ecosystemResponse = await industryApi.getSupportEcosystemDetails(id);
            setEcosystemData(ecosystemResponse);
          } catch (ecosystemErr) {
            console.error('Error fetching ecosystem data:', ecosystemErr);
            // Don't fail the whole page if ecosystem data fails
          }
        } else {
          setError('Industry not found');
        }
      } catch (err) {
        console.error('Error fetching industry:', err);
        setError('Failed to load industry data');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchIndustry();
    }

    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }, [id]);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleSubIndustryClick = (subId) => {
    navigate(`/industry/${id}/sub/${subId}`);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '70vh'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>Loading industry data...</Typography>
        </Box>
      </Box>
    );
  }

  if (error || !industry) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h5" color="error" gutterBottom>
            {error || 'Industry not found'}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/')}
            sx={{ mt: 2 }}
          >
            Back to Home
          </Button>
        </Box>
      </Container>
    );
  }

  const getTabIcon = (index) => {
    switch (index) {
      case 0: return <DashboardIcon />;
      case 1: return <GavelIcon />;
      case 2: return <LightbulbIcon />;
      case 3: return <TrendingUpIcon />;
      case 4: return <ScienceIcon />;
      default: return null;
    }
  };

  const getEcosystemIcon = (category) => {
    switch (category) {
      case 'places_infrastructure': return <BusinessIcon />;
      case 'events_networks': return <PeopleIcon />;
      case 'funding_incentives': return <MonetizationOnIcon />;
      case 'talent_innovation': return <ScienceIcon />;
      default: return <BusinessIcon />;
    }
  };

  const getEcosystemColor = (category) => {
    switch (category) {
      case 'places_infrastructure': return theme.palette.warning;
      case 'events_networks': return theme.palette.info;
      case 'funding_incentives': return theme.palette.success;
      case 'talent_innovation': return theme.palette.secondary;
      default: return theme.palette.primary;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs with improved styling */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" color="primary" />}
        aria-label="breadcrumb"
        sx={{
          mb: 4,
          '& .MuiBreadcrumbs-li': {
            display: 'flex',
            alignItems: 'center'
          }
        }}
      >
        <Link
          color="inherit"
          href="/"
          onClick={(e) => { e.preventDefault(); navigate('/'); }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            '&:hover': {
              color: theme.palette.primary.main
            }
          }}
        >
          Home
        </Link>
        <Typography color="text.primary" fontWeight="medium">{industry.name}</Typography>
      </Breadcrumbs>

      {/* Hero section with gradient background */}
      <MotionPaper
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        elevation={0}
        sx={{
          mb: 6,
          p: { xs: 3, md: 5 },
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40%',
            height: '100%',
            opacity: 0.1,
            background: `radial-gradient(circle, ${theme.palette.primary.main} 0%, transparent 70%)`,
            transform: 'translateX(30%)'
          }}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 3,
            position: 'relative',
            zIndex: 1
          }}
        >

          <Box>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1
                }}
              >
                {industry.name}
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{
                  maxWidth: '800px',
                  lineHeight: 1.6,
                  mb: 2
                }}
              >
                {industry.description}
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {['Industry Analysis', 'Market Research', 'Value Chain'].map((label, index) => (
                  <Chip
                    key={label}
                    label={label}
                    color={index === 0 ? 'primary' : undefined}
                    variant={index === 0 ? 'filled' : 'outlined'}
                    size="medium"
                    sx={{
                      borderRadius: 1.5,
                      fontWeight: 500,
                      border: index !== 0 ? `1.5px solid ${alpha(theme.palette.primary.main, 0.3)}` : undefined,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                      }
                    }}
                  />
                ))}
              </Box>
            </motion.div>
          </Box>
        </Box>
      </MotionPaper>

      {/* Market Summary Section */}
      <MotionPaper
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        elevation={2}
        sx={{
          mb: 6,
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
          backdropFilter: 'blur(10px)',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
          }}
        />

        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: 600,
            mb: 2,
            position: 'relative',
            display: 'inline-block',
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: '50%',
              height: 4,
              borderRadius: 2,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
            }
          }}
        >
          Market Summary
        </Typography>

        <Typography
          variant="body1"
          sx={{
            lineHeight: 1.8,
            color: theme.palette.text.primary,
            mt: 3
          }}
        >
          {industry.summary}
        </Typography>

      </MotionPaper>

      {/* Key Metrics Section - Sleek Integrated Card */}
      {(industry.marketSize || industry.growthRate !== undefined || (industry.growthData && industry.growthData.length > 0)) && (
        <MotionPaper
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          elevation={3}
          sx={{
            mb: 6,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.12)}`,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}
        >
          {/* Top gradient bar */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
            }}
          />

          {/* Header */}
          <Box sx={{ p: 4, pb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  mr: 2,
                  boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`
                }}
              >
                <InsightsIcon sx={{ fontSize: 28, color: 'white' }} />
              </Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                Key Market Metrics
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mx: 4 }} />

          {/* Content Grid */}
          <Box sx={{ p: 4 }}>
            <Grid container spacing={4}>
              {/* Left Column - Stats */}
              <Grid item xs={12} md={6}>
                <Grid container spacing={3}>
                  {/* Market Size */}
                  {industry.marketSize && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)}, ${alpha(theme.palette.info.light, 0.04)})`,
                          border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateX(4px)',
                            borderColor: alpha(theme.palette.info.main, 0.4)
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.light})`,
                              mr: 2,
                              boxShadow: `0 4px 12px ${alpha(theme.palette.info.main, 0.25)}`
                            }}
                          >
                            <MonetizationOnIcon sx={{ fontSize: 24, color: 'white' }} />
                          </Box>
                          <Typography variant="body2" color="text.secondary" fontWeight={600}>
                            Market Size
                          </Typography>
                        </Box>
                        <Typography
                          variant="h3"
                          sx={{
                            fontWeight: 700,
                            ml: 7,
                            background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                          }}
                        >
                          {industry.marketSize}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {/* Growth Rate */}
                  {industry.growthRate !== undefined && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.light, 0.04)})`,
                          border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateX(4px)',
                            borderColor: alpha(theme.palette.success.main, 0.4)
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.light})`,
                              mr: 2,
                              boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.25)}`
                            }}
                          >
                            <TrendingUpIcon sx={{ fontSize: 24, color: 'white' }} />
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary" fontWeight={600}>
                              Annual Growth Rate
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', ml: 7 }}>
                          <Typography
                            variant="h3"
                            sx={{
                              fontWeight: 700,
                              background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                              backgroundClip: 'text',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent'
                            }}
                          >
                            {industry.growthRate}%
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              ml: 1,
                              color: theme.palette.text.secondary
                            }}
                          >
                            YoY
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Grid>

              {/* Right Column - Growth Trend Chart */}
              {industry.growthData && industry.growthData.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.light, 0.03)})`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                          mr: 2,
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                        }}
                      >
                        <AssessmentIcon sx={{ fontSize: 24, color: 'white' }} />
                      </Box>
                      <Typography variant="body2" color="text.secondary" fontWeight={600}>
                        Historical Growth Trend
                      </Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1, minHeight: 240 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={industry.growthData}
                          margin={{ top: 10, right: 10, left: -10, bottom: 10 }}
                        >
                          <defs>
                            <linearGradient id="colorGrowth" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3} />
                              <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={alpha(theme.palette.divider, 0.2)}
                            vertical={false}
                          />
                          <XAxis
                            dataKey="year"
                            stroke={theme.palette.text.secondary}
                            style={{ fontSize: '0.75rem' }}
                            tickLine={false}
                          />
                          <YAxis
                            stroke={theme.palette.text.secondary}
                            style={{ fontSize: '0.75rem' }}
                            tickLine={false}
                            axisLine={false}
                          />
                          <Tooltip
                            contentStyle={{
                              background: theme.palette.background.paper,
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                              borderRadius: 8,
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="value"
                            stroke={theme.palette.primary.main}
                            strokeWidth={3}
                            dot={{ fill: theme.palette.primary.main, r: 5, strokeWidth: 2, stroke: '#fff' }}
                            activeDot={{ r: 7, strokeWidth: 2 }}
                            fillOpacity={1}
                            fill="url(#colorGrowth)"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </MotionPaper>
      )}

      {/* Opportunities and Trends Section */}
      {(industry.opportunities && industry.opportunities.length > 0) || (industry.trends && industry.trends.length > 0) ? (
        <Grid container spacing={4} sx={{ mb: 6 }}>
          {/* Opportunities Card */}
          {industry.opportunities && industry.opportunities.length > 0 && (
            <Grid item xs={12} md={6}>
              <MotionPaper
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                elevation={2}
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.light, 0.05)})`,
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }}
              >
                {/* Top gradient bar */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`
                  }}
                />

                <Box sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                        mr: 2,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                      }}
                    >
                      <StarsIcon sx={{ fontSize: 32, color: 'white' }} />
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}
                    >
                      Opportunities
                    </Typography>
                  </Box>

                  <List dense disablePadding>
                    {industry.opportunities.map((opportunity, idx) => (
                      <ListItem
                        key={idx}
                        sx={{
                          px: 0,
                          py: 1.5,
                          alignItems: 'flex-start',
                          borderBottom: idx !== industry.opportunities.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : 'none'
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                              color: 'white',
                              fontSize: 12,
                              fontWeight: 700,
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                            }}
                          >
                            {idx + 1}
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={opportunity}
                          primaryTypographyProps={{
                            variant: 'body1',
                            sx: {
                              lineHeight: 1.7,
                              color: theme.palette.text.primary,
                              fontWeight: 500
                            }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </MotionPaper>
            </Grid>
          )}

          {/* Trends Card */}
          {industry.trends && industry.trends.length > 0 && (
            <Grid item xs={12} md={6}>
              <MotionPaper
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                elevation={2}
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.08)}, ${alpha(theme.palette.secondary.light, 0.05)})`,
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                  boxShadow: `0 8px 24px ${alpha(theme.palette.secondary.main, 0.15)}`,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 32px ${alpha(theme.palette.secondary.main, 0.25)}`
                  }
                }}
              >
                {/* Top gradient bar */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light})`
                  }}
                />

                <Box sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light})`,
                        mr: 2,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.3)}`
                      }}
                    >
                      <ShowChartIcon sx={{ fontSize: 32, color: 'white' }} />
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}
                    >
                      Trends
                    </Typography>
                  </Box>

                  <List dense disablePadding>
                    {industry.trends.map((trend, idx) => (
                      <ListItem
                        key={idx}
                        sx={{
                          px: 0,
                          py: 1.5,
                          alignItems: 'flex-start',
                          borderBottom: idx !== industry.trends.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : 'none'
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light})`,
                              color: 'white',
                              fontSize: 12,
                              fontWeight: 700,
                              boxShadow: `0 2px 8px ${alpha(theme.palette.secondary.main, 0.3)}`
                            }}
                          >
                            {idx + 1}
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={trend}
                          primaryTypographyProps={{
                            variant: 'body1',
                            sx: {
                              lineHeight: 1.7,
                              color: theme.palette.text.primary,
                              fontWeight: 500
                            }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </MotionPaper>
            </Grid>
          )}
        </Grid>
      ) : null}

      {/* Enhanced tabs with new categories and icons */}
      <MotionBox
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        sx={{ width: '100%', mt: 6, mb: 8 }}
      >
        <Paper
          elevation={2}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
          }}
        >
          <Box
            sx={{
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
              }
            }}
          >
            <Tabs
              value={tab}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.secondary.main, 0.02)})`,
                '& .MuiTab-root': {
                  minHeight: 64,
                  px: 3,
                  fontWeight: 500,
                  textTransform: 'none',
                  fontSize: '1rem',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: alpha(theme.palette.primary.main, 0.08),
                    transform: 'translateY(-2px)'
                  },
                  '&.Mui-selected': {
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.secondary.main, 0.08)})`,
                    color: theme.palette.primary.main
                  }
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                }
              }}
            >
              {['Market Overview', 'Legal & Infrastructure', 'Opportunities & Incentives', 'Trends', 'Technology'].map((label, index) => (
                <Tab
                  key={label}
                  label={label}
                  icon={getTabIcon(index)}
                  iconPosition="start"
                />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ p: 4 }}>
            {/* Market Overview Tab */}
            <AnimatePresence mode="wait">
              {tab === 0 && (
                <MotionBox
                  key="market-overview"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    fontWeight={600}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    Market Overview
                  </Typography>

                {industry.tabs && industry.tabs.marketOverview ? (
                  <>
                    {/* Market Potential */}
                    {industry.tabs.marketOverview.marketPotential && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" fontWeight={600} color="primary.main" gutterBottom>
                          Market Potential
                        </Typography>
                        <List sx={{ mt: 2 }}>
                          {industry.tabs.marketOverview.marketPotential && industry.tabs.marketOverview.marketPotential.map((item, index) => (
                            <ListItem key={index} sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckIcon color="primary" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary={item} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}

                    {/* Critical Gaps */}
                    {industry.tabs.marketOverview.criticalGaps && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" fontWeight={600} color="primary.main" gutterBottom>
                          Critical Gaps
                        </Typography>
                        <List sx={{ mt: 2 }}>
                          {industry.tabs.marketOverview.criticalGaps && industry.tabs.marketOverview.criticalGaps.map((item, index) => (
                            <ListItem key={index} sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckIcon color="primary" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary={item} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}

                    {/* Critical Strengths */}
                    {industry.tabs.marketOverview.criticalStrengths && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" fontWeight={600} color="primary.main" gutterBottom>
                          Critical Strengths
                        </Typography>
                        <List sx={{ mt: 2 }}>
                          {industry.tabs.marketOverview.criticalStrengths && industry.tabs.marketOverview.criticalStrengths.map((item, index) => (
                            <ListItem key={index} sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckIcon color="primary" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary={item} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}

                    {/* Gaps Blocking Growth */}
                    {industry.tabs.marketOverview.gapsBlockingGrowth && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" fontWeight={600} color="primary.main" gutterBottom>
                          Gaps Blocking Growth
                        </Typography>
                        <List sx={{ mt: 2 }}>
                          {industry.tabs.marketOverview.gapsBlockingGrowth && industry.tabs.marketOverview.gapsBlockingGrowth.map((item, index) => (
                            <ListItem key={index} sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckIcon color="primary" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary={item} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}

                    {/* Investment Shortfalls */}
                    {industry.tabs.marketOverview.investmentShortfalls && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" fontWeight={600} color="primary.main" gutterBottom>
                          Investment Shortfalls
                        </Typography>
                        <List sx={{ mt: 2 }}>
                          {industry.tabs.marketOverview.investmentShortfalls && industry.tabs.marketOverview.investmentShortfalls.map((item, index) => (
                            <ListItem key={index} sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckIcon color="primary" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary={item} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}

                    {/* Bottom Line */}
                    {industry.tabs.marketOverview.bottomLine && (
                      <Box sx={{ mt: 4, p: 3, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          Bottom Line
                        </Typography>
                        <Typography variant="body1" paragraph>
                          {industry.tabs.marketOverview.bottomLine.text}
                        </Typography>
                        {industry.tabs.marketOverview.bottomLine.points && (
                          <List sx={{ mt: 1 }}>
                            {industry.tabs.marketOverview.bottomLine.points.map((point, index) => (
                              <ListItem key={index} sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <CheckIcon color="primary" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={point} />
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </Box>
                    )}

                    {/* National Strategy */}
                    {industry.tabs.marketOverview.nationalStrategy && (
                      <Box sx={{ mt: 4, p: 3, bgcolor: alpha(theme.palette.secondary.main, 0.05), borderRadius: 2, border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}` }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom color="secondary.main">
                          {industry.tabs.marketOverview.nationalStrategy.title}
                        </Typography>

                        {industry.tabs.marketOverview.nationalStrategy.published && (
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Published: {industry.tabs.marketOverview.nationalStrategy.published}
                          </Typography>
                        )}

                        {industry.tabs.marketOverview.nationalStrategy.vision && (
                          <>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
                              Vision
                            </Typography>
                            <Typography variant="body1" paragraph>
                              {industry.tabs.marketOverview.nationalStrategy.vision}
                            </Typography>
                          </>
                        )}

                        {industry.tabs.marketOverview.nationalStrategy.focusAreas && (
                          <>
                            <Typography variant="subtitle1" fontWeight={600}>
                              Focus Areas
                            </Typography>
                            <List>
                              {industry.tabs.marketOverview.nationalStrategy.focusAreas && industry.tabs.marketOverview.nationalStrategy.focusAreas.map((area, index) => (
                                <ListItem key={index} sx={{ py: 0.5 }}>
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    <CheckIcon color="secondary" fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText primary={area} />
                                </ListItem>
                              ))}
                            </List>
                          </>
                        )}

                        {industry.tabs.marketOverview.nationalStrategy.pillars && (
                          <>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
                              Pillars
                            </Typography>
                            <List>
                              {industry.tabs.marketOverview.nationalStrategy.pillars && industry.tabs.marketOverview.nationalStrategy.pillars.map((pillar, index) => (
                                <ListItem key={index} sx={{ py: 0.5 }}>
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    <CheckIcon color="secondary" fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText primary={pillar} />
                                </ListItem>
                              ))}
                            </List>
                          </>
                        )}

                        {industry.tabs.marketOverview.nationalStrategy.whyItMatters && (
                          <>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
                              Why It Matters
                            </Typography>
                            <List>
                              {industry.tabs.marketOverview.nationalStrategy.whyItMatters && industry.tabs.marketOverview.nationalStrategy.whyItMatters.map((matter, index) => (
                                <ListItem key={index} sx={{ py: 0.5 }}>
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    <CheckIcon color="secondary" fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText primary={matter} />
                                </ListItem>
                              ))}
                            </List>
                          </>
                        )}

                        {industry.tabs.marketOverview.nationalStrategy.documentLink && (
                          <Button
                            variant="outlined"
                            color="secondary"
                            sx={{ mt: 2 }}
                            href={industry.tabs.marketOverview.nationalStrategy.documentLink}
                            target="_blank"
                          >
                            View Full Strategy Document
                          </Button>
                        )}
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    Market overview data will be available soon.
                  </Typography>
                )}
                </MotionBox>
              )}

              {/* Legal & Infrastructure Tab */}
              {tab === 1 && (
                <MotionBox
                  key="legal-infrastructure"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    fontWeight={600}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    Legal & Infrastructure
                  </Typography>

                {industry.tabs && industry.tabs.legalInfrastructure ? (
                  <Grid container spacing={4}>
                    {/* Legal Section */}
                    <Grid item xs={12} md={6}>
                      <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom color="primary.main">
                          Legal Framework
                        </Typography>

                        {/* Regulatory Links */}
                        {industry.tabs.legalInfrastructure.legal?.regulatoryLinks && (
                          <Box sx={{ mt: 2 }}>
                            {industry.tabs.legalInfrastructure.legal.regulatoryLinks && industry.tabs.legalInfrastructure.legal.regulatoryLinks.map((section, idx) => (
                              <Box key={idx} sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" fontWeight={600}>{section.title}</Typography>
                                <List dense>
                                  {section.items && section.items.map((item, itemIdx) => (
                                    <ListItem key={itemIdx} sx={{ px: 0 }}>
                                      <ListItemIcon sx={{ minWidth: 32 }}>
                                        <CheckIcon color="primary" fontSize="small" />
                                      </ListItemIcon>
                                      <ListItemText
                                        primary={
                                          <Link href={item.link} target="_blank" color="primary">
                                            {item.name}
                                          </Link>
                                        }
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                              </Box>
                            ))}
                          </Box>
                        )}

                        {/* Notes */}
                        {industry.tabs.legalInfrastructure.legal?.notes && (
                          <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                              Notes
                            </Typography>
                            <List>
                              {industry.tabs.legalInfrastructure.legal.notes && industry.tabs.legalInfrastructure.legal.notes.map((note, index) => (
                                <ListItem key={index} sx={{ py: 0.5 }}>
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    <CheckIcon color="primary" fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText primary={note} />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}

                        {/* Foundations */}
                        {industry.tabs.legalInfrastructure.legal?.foundations && (
                          <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                              Foundations
                            </Typography>
                            <List>
                              {industry.tabs.legalInfrastructure.legal.foundations && industry.tabs.legalInfrastructure.legal.foundations.map((foundation, index) => (
                                <ListItem key={index} sx={{ py: 0.5 }}>
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    <CheckIcon color="primary" fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText primary={foundation} />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}
                      </Paper>
                    </Grid>

                    {/* Infrastructure Section */}
                    <Grid item xs={12} md={6}>
                      <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.secondary.main, 0.05), borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom color="secondary.main">
                          Infrastructure
                        </Typography>

                        {/* Physical Infrastructure */}
                        {industry.tabs.legalInfrastructure.infrastructure?.physical && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                              Physical Infrastructure
                            </Typography>
                            <List>
                              {industry.tabs.legalInfrastructure.infrastructure.physical && industry.tabs.legalInfrastructure.infrastructure.physical.map((item, index) => (
                                <ListItem key={index} sx={{ py: 0.5 }}>
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    <CheckIcon color="secondary" fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText primary={item} />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}

                        {/* Key Conflict */}
                        {industry.tabs.legalInfrastructure.infrastructure?.keyConflict && (
                          <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle1" fontWeight={600} color="error.main">
                              Key Conflict
                            </Typography>
                            <Typography variant="body1" color="error.main" sx={{ fontStyle: 'italic', mt: 1 }}>
                              {industry.tabs.legalInfrastructure.infrastructure.keyConflict}
                            </Typography>
                          </Box>
                        )}

                        {/* Digital Infrastructure */}
                        {industry.tabs.legalInfrastructure.infrastructure?.digital && (
                          <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                              Digital Infrastructure
                            </Typography>
                            <List>
                              {industry.tabs.legalInfrastructure.infrastructure.digital && industry.tabs.legalInfrastructure.infrastructure.digital.map((item, index) => (
                                <ListItem key={index} sx={{ py: 0.5 }}>
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    <CheckIcon color="secondary" fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText primary={item} />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}

                        {/* Priorities */}
                        {industry.tabs.legalInfrastructure.infrastructure?.priorities && (
                          <Box sx={{ mt: 4, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}` }}>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                              Priorities
                            </Typography>
                            <Typography variant="body2" paragraph>
                              {industry.tabs.legalInfrastructure.infrastructure.priorities.intro}
                            </Typography>
                            {industry.tabs.legalInfrastructure.infrastructure.priorities.items && (
                              <List>
                                {industry.tabs.legalInfrastructure.infrastructure.priorities.items.map((item, index) => (
                                  <ListItem key={index} sx={{ py: 0.5 }}>
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                      <CheckIcon color="secondary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary={item} />
                                  </ListItem>
                                ))}
                              </List>
                            )}
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    Legal and infrastructure data will be available soon.
                  </Typography>
                )}
                </MotionBox>
              )}

              {/* Opportunities & Incentives Tab */}
              {tab === 2 && (
                <MotionBox
                  key="opportunities"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    fontWeight={600}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    Opportunities & Incentives
                  </Typography>

                <Typography variant="body1" sx={{ maxWidth: '800px', lineHeight: 1.7, mb: 3 }}>
                  The Kingdom offers numerous incentives for {industry.name} investors, including tax benefits, subsidized
                  land and utilities, and financial support through institutions like the Saudi Industrial Development Fund.
                </Typography>

                {industry.tabs && industry.tabs.incentives ? (
                  <>
                    {/* Financial Incentives Section */}
                    {industry.tabs.incentives.content?.sections?.map((section, sectionIndex) => (
                      <Paper
                        key={sectionIndex}
                        elevation={1}
                        sx={{
                          mt: 3,
                          mb: 4,
                          p: 3,
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                        }}
                      >
                        <Typography
                          variant="h6"
                          fontWeight={600}
                          gutterBottom
                          color="primary.main"
                          sx={{
                            pb: 1,
                            borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                          }}
                        >
                          {section.title}
                        </Typography>

                        {/* If the section has subsections */}
                        {section.items ? (
                          section.items.map((item, itemIndex) => (
                            <Box key={itemIndex} sx={{ mt: 2 }}>
                              {item.subtitle && (
                                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                  {item.subtitle}
                                </Typography>
                              )}
                              <List dense>
                                {item.points && item.points.map((point, pointIndex) => (
                                  <ListItem key={pointIndex} sx={{ py: 0.5 }}>
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                      <CheckIcon color="primary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={
                                        <Typography variant="body2" dangerouslySetInnerHTML={{ __html: point }} />
                                      }
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          ))
                        ) : (
                          /* If the section has direct points */
                          <List dense>
                            {section.points && section.points.map((point, pointIndex) => (
                              <ListItem key={pointIndex} sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <CheckIcon color="primary" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Typography variant="body2" dangerouslySetInnerHTML={{ __html: point }} />
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </Paper>
                    ))}
                  </>
                ) : (
                  <Paper
                    elevation={1}
                    sx={{
                      mt: 3,
                      p: 3,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.warning.light, 0.1),
                      border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Detailed opportunities and incentives data will be available soon.
                    </Typography>
                  </Paper>
                )}
                </MotionBox>
              )}

              {/* Trends Tab */}
              {tab === 3 && (
                <MotionBox
                  key="trends"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    fontWeight={600}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    Trends
                  </Typography>

                {industry.tabs && industry.tabs.trends ? (
                  <>
                    {/* Trends List */}
                    {industry.tabs.trends.trendsList && (
                      <Grid container spacing={3} sx={{ mt: 1 }}>
                        {industry.tabs.trends.trendsList && industry.tabs.trends.trendsList.map((trend, index) => (
                          <Grid item xs={12} md={6} key={index}>
                            <Paper
                              elevation={1}
                              sx={{
                                p: 3,
                                borderRadius: 2,
                                height: '100%',
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                              }}
                            >
                              <Typography
                                variant="h6"
                                gutterBottom
                                sx={{
                                  fontWeight: 600,
                                  color: theme.palette.primary.main,
                                  pb: 1,
                                  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                                }}
                              >
                                {trend.name}
                              </Typography>

                              {trend.points && trend.points.map((point, pointIdx) => {
                                // Check if the point contains a colon to split into title and content
                                const hasSplit = point.includes(': ');
                                const [title, content] = hasSplit ? point.split(': ') : [null, point];

                                return (
                                  <Box key={pointIdx} sx={{ mt: 2 }}>
                                    {hasSplit && (
                                      <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                                        {title}:
                                      </Typography>
                                    )}
                                    <Typography variant="body2" color="text.secondary">
                                      {content}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    )}

                    {/* Where To Start */}
                    {industry.tabs.trends.whereToStart && (
                      <Box sx={{ mt: 4, p: 3, bgcolor: alpha(theme.palette.success.light, 0.1), borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom color="success.dark">
                          Where To Start
                        </Typography>
                        <List>
                          {industry.tabs.trends.whereToStart && industry.tabs.trends.whereToStart.map((item, index) => (
                            <ListItem key={index} sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <CheckIcon color="success" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary={item} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    Trend data will be available soon.
                  </Typography>
                )}
                </MotionBox>
              )}

              {/* Technology Tab */}
              {tab === 4 && (
                <MotionBox
                  key="technology"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    fontWeight={600}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    Technology
                  </Typography>

                  {industry.tabs && industry.tabs.technology ? (
                    <>
                      {/* Technology Assets */}
                      {industry.tabs.technology.assets && (
                        <Grid container spacing={3} sx={{ mt: 1 }}>
                          {industry.tabs.technology.assets.map((asset, index) => (
                            <Grid item xs={12} md={6} key={index}>
                              <Paper
                                elevation={1}
                                sx={{
                                  p: 3,
                                  borderRadius: 2,
                                  height: '100%',
                                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                                  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.secondary.main, 0.02)})`,
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: `0 8px 24px ${alpha(theme.palette.secondary.main, 0.15)}`,
                                    borderColor: alpha(theme.palette.secondary.main, 0.4)
                                  }
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                  <Box
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      borderRadius: 1.5,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light})`,
                                      mr: 2,
                                      boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.3)}`
                                    }}
                                  >
                                    <ScienceIcon sx={{ fontSize: 24, color: 'white' }} />
                                  </Box>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 600,
                                      color: theme.palette.secondary.main
                                    }}
                                  >
                                    {asset.name}
                                  </Typography>
                                </Box>

                                {asset.description && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mb: 2, fontStyle: 'italic' }}
                                  >
                                    {asset.description}
                                  </Typography>
                                )}

                                {asset.capabilities && (
                                  <List dense disablePadding sx={{ mt: 2 }}>
                                    {asset.capabilities.map((capability, capIdx) => (
                                      <ListItem key={capIdx} sx={{ px: 0, py: 0.5 }}>
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                          <CheckIcon color="secondary" fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText
                                          primary={capability}
                                          primaryTypographyProps={{
                                            variant: 'body2',
                                            sx: { lineHeight: 1.6 }
                                          }}
                                        />
                                      </ListItem>
                                    ))}
                                  </List>
                                )}
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      )}

                      {/* Emerging Technologies */}
                      {industry.tabs.technology.emergingTechnologies && (
                        <Box sx={{ mt: 4, p: 3, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 2, border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }}>
                          <Typography variant="h6" fontWeight={600} gutterBottom color="info.main">
                            {industry.tabs.technology.emergingTechnologies.title}
                          </Typography>
                          {industry.tabs.technology.emergingTechnologies.description && (
                            <Typography variant="body2" color="text.secondary" paragraph>
                              {industry.tabs.technology.emergingTechnologies.description}
                            </Typography>
                          )}
                          <List>
                            {industry.tabs.technology.emergingTechnologies.technologies && industry.tabs.technology.emergingTechnologies.technologies.map((tech, index) => (
                              <ListItem key={index} sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <CheckIcon color="info" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={tech} />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}

                      {/* Implementation Priorities */}
                      {industry.tabs.technology.implementationPriorities && (
                        <Box sx={{ mt: 4, p: 3, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 2, border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                          <Typography variant="h6" fontWeight={600} gutterBottom color="success.dark">
                            {industry.tabs.technology.implementationPriorities.title}
                          </Typography>
                          {industry.tabs.technology.implementationPriorities.intro && (
                            <Typography variant="body2" color="text.secondary" paragraph>
                              {industry.tabs.technology.implementationPriorities.intro}
                            </Typography>
                          )}
                          <List>
                            {industry.tabs.technology.implementationPriorities.priorities && industry.tabs.technology.implementationPriorities.priorities.map((priority, index) => (
                              <ListItem key={index} sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <CheckIcon color="success" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={priority} />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      Technology data will be available soon.
                    </Typography>
                  )}
                </MotionBox>
              )}
            </AnimatePresence>
          </Box>
        </Paper>
      </MotionBox>

      {/* Value Chain section with enhanced UI */}
      <MotionPaper
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        elevation={1}
        sx={{
          mt: 4,
          mb: 8,
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`
        }}
      >
        <Box
          sx={{
            p: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at top right, rgba(255,255,255,0.2), transparent 70%)',
              pointerEvents: 'none'
            }
          }}
        >
          <Typography variant="h4" fontWeight={600} sx={{ position: 'relative', zIndex: 1 }}>
            Value Chain
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.95, mt: 0.5, position: 'relative', zIndex: 1 }}>
            Model of the Value Chain. Each name is a separate page with data.
          </Typography>
        </Box>

        <Box p={3}>
          <ValueChainTable
            valueChainId={industry.id}
            valueChainData={industry.valueChain}
            industryName={industry.name}
            industryId={industry.id}
            subIndustries={industry.subIndustries || []}
          />
        </Box>
      </MotionPaper>

      {/* Support Ecosystem Section */}
      <MotionPaper
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        elevation={1}
        sx={{
          mt: 4,
          mb: 8,
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`
        }}
      >
        <Box
          sx={{
            p: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at top right, rgba(255,255,255,0.2), transparent 70%)',
              pointerEvents: 'none'
            }
          }}
        >
          <Typography variant="h4" fontWeight={600} sx={{ position: 'relative', zIndex: 1 }}>
            Support Ecosystem
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.95, mt: 0.5, position: 'relative', zIndex: 1 }}>
            Critical supporting elements for successful industry development
          </Typography>
        </Box>

        <Box p={3}>
          <Grid container spacing={4}>
            {ecosystemData && ecosystemData.map((ecosystem, index) => {
              const color = getEcosystemColor(ecosystem.category);
              const icon = getEcosystemIcon(ecosystem.category);

              return (
                <Grid item xs={12} md={3} key={ecosystem.id}>
                  <MotionCard
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.1 * (index + 1) }}
                    whileHover={{ y: -8, scale: 1.03 }}
                    elevation={2}
                    sx={{
                      height: '100%',
                      borderRadius: 3,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(color.main, 0.03)})`,
                      border: `1px solid ${alpha(color.main, 0.2)}`,
                      boxShadow: `0 4px 20px ${alpha(color.main, 0.1)}`,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        boxShadow: `0 12px 40px ${alpha(color.main, 0.25)}`,
                        border: `1px solid ${alpha(color.main, 0.4)}`
                      }
                    }}
                  >
                    <CardActionArea
                      sx={{
                        height: '100%',
                        borderTop: `3px solid ${color.main}`,
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          background: `linear-gradient(90deg, ${color.main}, ${color.light})`,
                          opacity: 0,
                          transition: 'opacity 0.3s ease'
                        },
                        '&:hover::before': {
                          opacity: 1
                        }
                      }}
                      onClick={() => navigate(`/support-ecosystem/${ecosystem.id}`)}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ mr: 1.5, fontSize: 28, color: color.main }}>
                            {icon}
                          </Box>
                          <Typography variant="h6" fontWeight={600} sx={{ color: color.main }}>
                            {ecosystem.name}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                          {ecosystem.leadText}
                        </Typography>
                        <Divider sx={{ my: 1.5 }} />
                        <List dense disablePadding>
                          {ecosystem.bulletPoints && ecosystem.bulletPoints.slice(0, 4).map((point, idx) => (
                            <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 28 }}>
                                <CheckIcon sx={{ color: color.main }} fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary={point}
                                primaryTypographyProps={{
                                  variant: 'body2',
                                  sx: {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical'
                                  }
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 2 }}>
                          <Typography variant="button" sx={{ display: 'flex', alignItems: 'center', color: color.main }}>
                            Explore
                            <ArrowForwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </MotionCard>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </MotionPaper>

      {/* Sub-industries with improved cards */}
      {industry.hasSubIndustries && (
        <MotionBox
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          mb={8}
        >
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: 600,
              mb: 3,
              position: 'relative',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: 0,
                width: 60,
                height: 4,
                borderRadius: 2,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
              }
            }}
          >
            Sub-Industries
          </Typography>

          <Grid container spacing={3}>
            {industry.subIndustries && industry.subIndustries.map((subIndustry, index) => (
              <Grid item xs={12} sm={6} md={4} key={subIndustry.id}>
                <MotionCard
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  whileHover={{ y: -12, scale: 1.02 }}
                  elevation={2}
                  sx={{
                    borderRadius: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.2)}`,
                      '& .gradient-top': {
                        height: 6
                      }
                    }
                  }}
                >
                  <CardActionArea
                    onClick={() => handleSubIndustryClick(subIndustry.id)}
                    sx={{
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      height: '100%'
                    }}
                  >
                    <Box
                      className="gradient-top"
                      sx={{
                        height: 4,
                        width: '100%',
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        transition: 'height 0.3s ease'
                      }}
                    />

                    <CardContent sx={{ p: 3, flexGrow: 1, width: '100%' }}>
                      <Typography
                        variant="h6"
                        sx={{
                          mb: 1.5,
                          fontWeight: 600,
                          color: theme.palette.text.primary
                        }}
                      >
                        {subIndustry.name}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2, lineHeight: 1.6 }}
                      >
                        {subIndustry.description}
                      </Typography>

                      <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', color: theme.palette.primary.main }}>
                        <Typography variant="button" sx={{ mr: 0.5, fontWeight: 600 }}>
                          Explore
                        </Typography>
                        <ArrowForwardIcon fontSize="small" />
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </MotionCard>
              </Grid>
            ))}
          </Grid>
        </MotionBox>
      )}      

      {/* Related Content - Enhanced Grid */}
      <MotionBox
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        mt={8}
        mb={4}
      >
        <Typography
          variant="h4"
          sx={{
            mb: 4,
            fontWeight: 600,
            position: 'relative',
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 60,
              height: 4,
              borderRadius: 2,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
            }
          }}
        >
          Related Content
        </Typography>

        <Grid container spacing={3}>
          {[
            {
              title: 'Industry Experts',
              icon: <PeopleIcon sx={{ fontSize: 48, color: 'white' }} />,
              description: 'Connect with leading professionals in this industry',
              path: `/experts?industry=${industry.id}`
            },
            {
              title: 'Investment Offers',
              icon: <MonetizationOnIcon sx={{ fontSize: 48, color: 'white' }} />,
              description: 'Explore current investment opportunities',
              path: `/marketplace`
            },
            {
              title: 'Innovation Hub',
              icon: <ScienceIcon sx={{ fontSize: 48, color: 'white' }} />,
              description: 'Discover the latest in this industry R&D',
              path: `/articles?type=innovation&industry=${industry.id}`
            },
            {
              title: 'Industry News',
              icon: <ArticleIcon sx={{ fontSize: 48, color: 'white' }} />,
              description: 'Stay updated with market trends',
              path: `/articles?type=news&industry=${industry.id}`
            }
          ].map((item, index) => (
            <Grid item xs={12} sm={6} lg={3} key={index}>
              <MotionCard
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                whileHover={{ y: -8, scale: 1.03 }}
                elevation={2}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                  }
                }}
              >
                <CardActionArea
                  sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
                  onClick={() => navigate(item.path)}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: 120,
                      background: index % 2 === 0
                        ? `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.8)}, ${alpha(theme.palette.primary.main, 0.9)})`
                        : `linear-gradient(135deg, ${alpha(theme.palette.secondary.light, 0.8)}, ${alpha(theme.palette.secondary.main, 0.9)})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'radial-gradient(circle at center, rgba(255,255,255,0.2), transparent 70%)',
                        opacity: 0,
                        transition: 'opacity 0.3s ease'
                      },
                      '&:hover::before': {
                        opacity: 1
                      }
                    }}
                  >
                    {item.icon}
                  </Box>

                  <CardContent sx={{ p: 3, flexGrow: 1 }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {item.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </MotionCard>
            </Grid>
          ))}
        </Grid>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              endIcon={<ArrowForwardIcon />}
              size="large"
              onClick={() => navigate(`/industry/${industry.id}/resources`)}
              sx={{
                borderRadius: 2,
                py: 1.5,
                px: 5,
                textTransform: 'none',
                fontWeight: 600,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`
                }
              }}
            >
              Explore All Content
            </Button>
          </Box>
        </motion.div>
      </MotionBox>
    </Container>
  );
};

export default IndustryPage;